import { useEffect, useRef } from "react";

import {
  buildCaptureEligibilityEvent,
  emitCaptureEligibility,
  isSameCaptureEligibilityEvent,
} from "@/helpers/observe-capture-metrics";
import { isSnowplowUserIdKnown } from "@/helpers/snowplow";
import { useSnowplowUserId } from "@/hooks/useSnowplowUserId";
import { useFeatureFlagsContext } from "@/stores/contexts/FeatureFlagsContext";
import {
  type CaptureEligibilityEvent,
  type CaptureEligibilityMetricInput,
} from "@/types/observeCapture";

/**
 * Sends one CAPTURE-ELIGIBILITY event each time the reason or the bridge presence changes.
 *
 * Sends nothing until the Snowplow user id, the flags and the step are known. The flags are
 * known once the flags query succeeded one time, so a later failed refetch changes nothing.
 * `useFeatureFlag` reads false until the flags load, so an earlier send is a false
 * `CAPTURE-FLAG-OFF`.
 *
 * @param input The flag state, the bridge presence and the step.
 */
export const useCaptureEligibilityMetric = ({
  isEnabled,
  hasBridge,
  stepId,
}: CaptureEligibilityMetricInput): void => {
  const isUserIdKnown = isSnowplowUserIdKnown(useSnowplowUserId());
  const { hasLoaded } = useFeatureFlagsContext();
  const lastSentEvent = useRef<CaptureEligibilityEvent>();

  useEffect(() => {
    if (!isUserIdKnown || !hasLoaded || stepId === undefined) {
      return;
    }
    const event = buildCaptureEligibilityEvent({
      isEnabled,
      hasBridge,
      stepId,
    });
    if (isSameCaptureEligibilityEvent(lastSentEvent.current, event)) {
      return;
    }
    lastSentEvent.current = event;
    emitCaptureEligibility(event);
  }, [isEnabled, hasBridge, stepId, isUserIdKnown, hasLoaded]);
};
